import { computed, ref, set, watch } from "@vue/composition-api";
import { FileIdUpload } from "@scrile/api-provider/dist/api/FileProvider";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useBlobUrl from "@/hooks/useBlobUrl";
import useFiles, { UploadFilesData } from "@/hooks/useFiles";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import {
  FileType,
  getFileMimeType,
  isAttachmentFile,
  isAudioFile,
  isDocumentFile,
  isInputAttachmentFile,
  isTextFile,
} from "@/lib/fileHelpers";
import { calculateProgress } from "@/lib/FileUploader";
import { getMediaThumbnailUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { AttachmentFile, InputAttachmentFile } from "@/types";

export const UID_PREFIX = "uploading_";

const useMessageAttachments = () => {
  const { isMember } = useAuthUser();
  const { uploadFilesToS3, stopFileUploading, getUploadUrl } = useFiles();
  const { getBlobUrl, revokeBlobs } = useBlobUrl();
  const { paymentPackages: priceList, getPaymentPackages } = usePaymentPackages([
    PaymentPackageEnum.MESSAGE_ATTACHMENT,
  ]);

  const fileIdUpload = ref<FileIdUpload[]>([]);
  const attachedFiles = ref<AttachmentFile[]>([]);
  const selectedPriceId = ref("0");

  const privateFiles = computed(() => attachedFiles.value.filter(f => !f.publicAccess));
  const publicFiles = computed(() => attachedFiles.value.filter(f => f.publicAccess));
  const uploading = computed(
    () => !!fileIdUpload.value.length || attachedFiles.value.some(f => f.uid.startsWith(UID_PREFIX))
  );

  const selectPrice = (id: string) => (selectedPriceId.value = id);

  const getSrc = (file: FileType, urlPart: string): string => {
    if (isDocumentFile(file) || isTextFile(file)) return "";
    if (isAudioFile(file) && file instanceof File) return getBlobUrl(file);
    return getMediaThumbnailUrl(file, urlPart);
  };

  const maxCountPrivateFiles = computed(() =>
    isMember.value ? config.attachments.MAX_FILES_UPLOAD_FOR_MEMBER : config.attachments.MAX_FILES_UPLOAD_FOR_CREATOR
  );
  const maxCountPublicFiles = computed(() => (isMember.value ? 0 : config.attachments.MAX_COVER_FILES));
  const allowedPublicFilesCount = computed(() => maxCountPublicFiles.value - publicFiles.value.length);
  const allowedPrivateFilesCount = computed(() => maxCountPrivateFiles.value - privateFiles.value.length);

  const getLimitedFiles = (files: InputAttachmentFile[]): InputAttachmentFile[] => {
    if (files[0].publicAccess) {
      if (isAudioFile(files[0].file)) return files; // todo: make correct guard assertion and check what for is it here
      return files.filter((_, index) => index + 1 <= allowedPublicFilesCount.value);
    } else {
      return files.filter((_, index) => index + 1 <= allowedPrivateFilesCount.value);
    }
  };

  const shapeFilesAndFiledIds = async (files: InputAttachmentFile[]): Promise<FileIdUpload[]> => {
    let fileIds: FileIdUpload[] = [];
    for (const [index, data] of files.entries()) {
      const uploadUrl = await getUploadUrl([{ subjectId: String(index + 1), type: "message_attachment" }]);

      attachedFiles.value[data.publicAccess ? "unshift" : "push"]({
        type: getFileMimeType(data.file),
        uid: UID_PREFIX + uploadUrl[0].fileUpload.uuid,
        src: getSrc(data.file, uploadUrl[0].fileUpload.signedUrlPart),
        publicAccess: data.publicAccess,
        uploadProgress: 0,
      });
      fileIds = fileIds.concat(uploadUrl);
    }
    return fileIds;
  };

  const removeFileIdItemByUid = (uid: string): void => {
    const uidWithoutPrefix = uid.replace(UID_PREFIX, "");
    fileIdUpload.value = fileIdUpload.value.filter(i => i.fileUpload.uuid !== uidWithoutPrefix);
  };

  const successFileUpload = (uid: string): void => {
    attachedFiles.value = attachedFiles.value.map(i => {
      if (i.uid.includes(UID_PREFIX + uid)) {
        i.uid = uid;
        i.uploadProgress = 100;
      }
      return i;
    });
    removeFileIdItemByUid(uid);
  };

  const addInputAttachmentFiles = async (files: InputAttachmentFile[]) => {
    getPaymentPackages().then();
    if (files.length === 0) return;

    const limitedFiles: InputAttachmentFile[] = getLimitedFiles(files);
    const filesToUpload: InputAttachmentFile[] = Array.from(limitedFiles);
    fileIdUpload.value = await shapeFilesAndFiledIds(filesToUpload);

    const requestData = fileIdUpload.value.map<UploadFilesData>((fileId, index) => {
      return {
        file: filesToUpload[index].file,
        fileIdUpload: fileId,
        onError: (_, uid) => removeFileIdItemByUid(uid),
        onAbortUploading: removeFileIdItemByUid,
        onSuccess: successFileUpload,
        onUploadProgress: p => {
          const index = attachedFiles.value.findIndex(item => item.uid === UID_PREFIX + fileId.fileUpload.uuid);
          if (index !== -1) {
            set(attachedFiles.value[index], "uploadProgress", calculateProgress(p));
          }
        },
      };
    });
    uploadFilesToS3(requestData);
  };

  const isFileNotUnique = (file: AttachmentFile) => {
    return attachedFiles.value.some(f => f.uid === file.uid);
  };
  const addAttachmentFiles = async (files: AttachmentFile[]) => {
    getPaymentPackages().then();
    files.forEach(file => {
      if (
        (file.publicAccess && allowedPublicFilesCount.value === 0) ||
        (!file.publicAccess && allowedPrivateFilesCount.value === 0) ||
        isFileNotUnique(file)
      ) {
        return;
      }
      attachedFiles.value[file.publicAccess ? "unshift" : "push"](file);
    });
  };

  const addAttachment = async (files: (AttachmentFile | InputAttachmentFile)[]): Promise<void> => {
    const attachmentFiles = files.filter(isAttachmentFile);
    const inputFiles = files.filter(isInputAttachmentFile);
    inputFiles.length > 0 && (await addInputAttachmentFiles(inputFiles));
    attachmentFiles.length > 0 && (await addAttachmentFiles(attachmentFiles));
  };

  const clearAttachments = () => {
    attachedFiles.value = [];
    selectedPriceId.value = "0";
    revokeBlobs();
  };

  const remove = (file?: AttachmentFile) => {
    if (file) {
      stopFileUploading(file.uid);
      removeFileIdItemByUid(file.uid);
      attachedFiles.value = attachedFiles.value.filter(f => f.uid !== file.uid);
      if (selectedPriceId.value !== "0" && privateFiles.value.length === 0 && publicFiles.value.length > 0) {
        clearAttachments();
      }
    } else clearAttachments();
  };

  const waitFileUpload = async (): Promise<void> => {
    return new Promise(resolve => {
      if (!uploading.value) {
        resolve();
        return;
      }
      const unwatch = watch(uploading, () => {
        if (!uploading.value) {
          unwatch();
          resolve();
        }
      });
    });
  };

  return {
    uploading,
    priceList,
    attachedFiles,
    hasAttachments: computed(() => attachedFiles.value.length > 0),
    addAttachment,
    remove,
    selectPrice,
    selectedPriceId,
    allowedPublicFilesCount,
    allowedPrivateFilesCount,
    waitFileUpload,
  };
};

export default useMessageAttachments;
